<template>
  <section class="new-report__container">
    <intro />
    <error-messages />
    <form
      id="newIncidentReport"
      enctype="multipart/form-data"
      class="new-report__form"
      @submit.prevent="postForm"
    >
      <contact-information />
      <location />
      <dead-number />
      <sick-number />
      <sick-signs />
      <water-proximity />
      <photo :mode="mode" />

      <admin v-if="isLoggedIn()" />

      <section class="new-report__section">
        <div
          class="g-recaptcha"
          data-sitekey="6Le8KxcTAAAAAAv4_Cyfg5PYUjcWujAkffIOescf"
        ></div>
      </section>

      <button
        v-if="$props.mode !== 'view'"
        id="submit"
        ref="submit"
        :class="{ disabled: !requiredFieldsCompleted }"
        class="button new-report__submit-button"
        :disabled="!requiredFieldsCompleted"
        type="submit"
      >
        Submit Report
      </button>
    </form>
  </section>
</template>

<script>
import { get } from "lodash";
import { mapMutations } from "vuex";
import Intro from "./Components/Intro";
import ErrorMessages from "./Components/ErrorMessages";
import ContactInformation from "./Components/ContactInformation";
import Location from "./Components/Location";
import DeadNumber from "./Components/DeadNumber";
import SickNumber from "./Components/SickNumber";
import SickSigns from "./Components/SickSigns";
import WaterProximity from "./Components/WaterProximity";
import Photo from "./Components/Photo";
import Admin from "./Components/Admin";

import counties from "~/components/Reports/counties";

export default {
  name: "IncidentReportForm",
  components: {
    Intro,
    ErrorMessages,
    ContactInformation,
    Location,
    DeadNumber,
    SickNumber,
    SickSigns,
    WaterProximity,
    Photo,
    Admin,
  },
  props: {
    mode: {
      type: String,
    },
  },
  computed: {
    symptoms() {
      // When I tried looping through with some or forEach or
      // other methods the page would crash when I tested the symptoms selection
      // Thankfully these arrays are short
      if (
        this.$store.state.incidentReport.symptoms["other"] ||
        this.$store.state.incidentReport.symptoms["symptom1"] ||
        this.$store.state.incidentReport.symptoms["symptom2"] ||
        this.$store.state.incidentReport.symptoms["symptom3"] ||
        this.$store.state.incidentReport.symptoms["symptom4"] ||
        this.$store.state.incidentReport.symptoms["symptom5"] ||
        this.$store.state.incidentReport.symptoms["symptom6"] ||
        this.$store.state.incidentReport.symptoms["symptom7"] ||
        this.$store.state.incidentReport.symptoms["symptom8"]
      ) {
        return true;
      }
      return false;
    },
    waterProximity() {
      if (
        this.$store.state.incidentReport.waterProximity["water_deer0"] ||
        this.$store.state.incidentReport.waterProximity["water_deer1"] ||
        this.$store.state.incidentReport.waterProximity["water_deer2"] ||
        this.$store.state.incidentReport.waterProximity["water_deer3"] ||
        this.$store.state.incidentReport.waterProximity["water_deer4"] ||
        this.$store.state.incidentReport.waterProximity["water_deer5"]
      ) {
        return true;
      }
      return false;
    },
    incidentReport() {
      return this.$store.state.incidentReport;
    },
    deadDeerFields() {
      return !!(
        this.incidentReport.deadDeer !== null &&
        this.incidentReport.deadDeer !== "None" &&
        this.waterProximity
      );
    },
    sickDeerFields() {
      return !!(
        this.incidentReport.sickDeer !== null &&
        this.incidentReport.sickDeer !== "None" &&
        this.symptoms
      );
    },
    contactInformationDefined() {
      if (
        Object.values(this.incidentReport.contactInformation).every(
          (value) => value !== null
        )
      ) {
        return true;
      }
      return false;
    },
    // After filling in location details it did not automatically enable the submit button need to check on that!
    locationDefined() {
      if (
        Object.values(this.incidentReport.location).every(
          (value) => value !== null
        )
      ) {
        return true;
      }
      return false;
    },
    requiredFieldsCompleted() {
      if (
        this.contactInformationDefined &&
        this.locationDefined &&
        (this.deadDeerFields || this.sickDeerFields)
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    postForm() {
      const incidentReport = this.$store.state.incidentReport;
      const {
        contactInformation,
        location,
        deadDeer,
        sickDeer,
        symptoms,
        waterProximity,
        photos,
        admin,
      } = incidentReport;
      const { name, email, phone } = contactInformation;
      const { county, details } = location;
      const {
        symptom1,
        symptom2,
        symptom3,
        symptom4,
        symptom5,
        symptom6,
        symptom7,
        symptom8,
        other,
      } = symptoms;
      const {
        water_deer0,
        water_deer1,
        water_deer2,
        water_deer3,
        water_deer4,
        water_deer5,
      } = waterProximity;

      const submit = this.$refs.submit;
      submit.setAttribute("disabled", "disabled");
      const formData = new FormData();
      if (get(this, "$store.state.route.params.pathMatch")) {
        formData.append("id", get(this, "$store.state.route.params.pathMatch"));
      }
      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("county", county);
      formData.append("locDetails", details);
      formData.append("deadRange", deadDeer);
      formData.append("sickRange", sickDeer);
      formData.append("symptom1", symptom1);
      formData.append("symptom2", symptom2);
      formData.append("symptom3", symptom3);
      formData.append("symptom4", symptom4);
      formData.append("symptom5", symptom5);
      formData.append("symptom6", symptom6);
      formData.append("symptom7", symptom7);
      formData.append("symptom8", symptom8);
      formData.append("symptomOther", other);
      formData.append("water_deer0", water_deer0);
      formData.append("water_deer1", water_deer1);
      formData.append("water_deer2", water_deer2);
      formData.append("water_deer3", water_deer3);
      formData.append("water_deer4", water_deer4);
      formData.append("water_deer5", water_deer5);
      Array.from(photos).forEach((p) => {
        formData.append("photos", p);
      });
      formData.append("actionTaken", get(admin, "actionTaken.value"));
      formData.append("biologist", admin.biologist);
      formData.append("dateHandled", admin.dateHandled);
      formData.append("incidentType", admin.incidentType);
      formData.append("notes", admin.notes);
      this.$axios
        .post("/api/incidents", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          console.log("axios res", res);
          window.location = "/reports";
        })
        .catch((error) => {
          console.log("axios error", error);
          if (get(error, "response.data.errors")) {
            this.inputErrors = error.response.data.errors;
          }
        })
        .finally(() => submit.removeAttribute("disabled"));
    },
    isLoggedIn() {
      return !!this.$cookies.get("token");
    },
  },
};
</script>

<style lang="scss">
.new-report {
  font-family: $font-sans;
  &__section {
    position: relative;
    width: 100%;
    & ~ & {
      margin-top: 1.75rem;
    }
  }
  &__form {
    margin-top: 2rem;
    width: 100%;
  }
  &__input-row {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }
  &__label {
    width: calc(100% / 3);
    @media only screen and (min-width: 768px) {
      width: calc(100% / 6);
    }
  }
  &__input {
    width: calc(200% / 3);
    @media only screen and (min-width: 768px) {
      width: 90%;
    }
    &--textarea {
      border: solid black 1px;
      min-height: 60px;
      padding: 0.5rem;
    }
    &--text {
      border-color: black;
      border-width: 1px;
      font-size: 14px;
      padding: 0 0.5rem;
      min-height: 40px;
    }
    &--radio {
      appearance: none;
      height: 0;
      width: 0;
    }
  }
  ::placeholder {
    color: #adadad;
  }
  &__multiselect-container {
    align-content: center;
    display: flex;
  }
  &__checkbox-container {
    @include tablet {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-auto-rows: 30px;
      grid-gap: 0.75rem;
    }
  }
  &__date-internal {
    height: 25px;
    width: 100%;
    border: solid black 1px;
  }
  &__radio-label {
    background-color: get-color("green", 300);
    color: white;
    flex-basis: calc(100% / 6);
    margin: 0;
    padding: 0.25rem 0;
    text-align: center;
    & ~ & {
      margin-left: 1rem;
    }
    .new-report__input--radio:focus + & {
      outline: get-color("green") auto 5px;
    }
    .new-report__input--radio:checked + & {
      background-color: get-color("green");
    }
  }
  &__required-marker {
    color: red;
  }
  &__heading {
    color: get-color("brown");
    font-family: $font-serif;
    font-size: 1.3rem;
    font-weight: 300;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
  }
  &__submit-button {
    background-color: get-color("green");
    border: 1px solid get-color("grey", 300);
    color: white;
    display: inline-block;
    line-height: normal;
    padding: 0.5em 1em;
    text-decoration: none;
    transition: all 0.3s;
    width: 100%;
    font-family: $font-sans;
    font-size: 18px;
    font-weight: 100;
    margin-bottom: 1rem;
    &:hover {
      background-color: get-color("blue");
    }
    &:disabled {
      background-color: $darkestgrey;
    }
  }
}
</style>
